/* @jsxImportSource react */

export const loaderFragment = /* glsl */ `
#define PI 3.14159265358979323

#define RADIUS 0.4
#define THICKNESS 0.05
#define INNER_RADIUS RADIUS - THICKNESS
#define CIRCLE_RADIUS THICKNESS / 2.0

uniform float loaded;
uniform sampler2D image;
uniform float imageAspectRatio;
uniform float imageScale;
uniform vec3 ringColor;

varying vec2 vUv;

vec2 rotate(vec2 v, float a) {
	float s = sin(a);
	float c = cos(a);
	mat2 m = mat2(c, -s, s, c);
	return m * v;
}

float radialGradient(vec2 uv) {
    return atan(uv.y, uv.x) * 0.5 / PI + 0.5;
}

float circle(vec2 uv, float radius) {
    return step(length(uv), radius);
}

float ring(vec2 uv, float outerRadius, float innerRadius) {
    return circle(uv, outerRadius) - circle(uv, innerRadius);
}

vec2 polarToCartesian(float angle, float radius) {
    return vec2(cos(angle), sin(angle)) * radius;
}

float when_gt(float x, float y) {
    return max(sign(x - y), 0.0);
} 
  
float when_lt(float x, float y) {
    return max(sign(y - x), 0.0);
} 

float when_ge(float x, float y) {
    return 1.0 - when_lt(x, y);
}

float when_le(float x, float y) {
    return 1.0 - when_gt(x, y);
}

vec4 fitImage(sampler2D sampler, vec2 uv, float aspectRatio, float scale) {
    vec2 imageUV = uv;

    // center
    imageUV -= vec2(0.5);

    // Avoid if/else branching
    float multiplier = mix(
        1.0 / aspectRatio, 
        aspectRatio, 
        when_ge(aspectRatio, 1.0)
    );
    imageUV.y *= multiplier;

    imageUV /= scale;

    // un-center
    imageUV += vec2(0.5);

    // Avoid if/else branching
    float withinBounds = 
        when_ge(imageUV.x, 0.0) * 
        when_ge(imageUV.y, 0.0) * 
        when_le(imageUV.x, 1.0) *
        when_le(imageUV.y, 1.0);

    vec4 imageColor = texture2D(sampler, imageUV);
    imageColor.a *= withinBounds;
    return imageColor;
}

void main() {
    vec2 centered = vUv - vec2(0.5);

    vec2 rotated = rotate(centered, radians(-90.0));
    float radialGradient = radialGradient(rotated);

    float angle = mix(fract(loaded), 1.0, when_gt(loaded, 0.999));
  
    float loadedSlice = step(1.0 - radialGradient, angle);
    float mask = ring(centered, RADIUS, INNER_RADIUS) * loadedSlice;

    vec2 startCircleOffset = polarToCartesian(0.0, RADIUS - CIRCLE_RADIUS);
    float startCircle = circle(rotated + startCircleOffset, CIRCLE_RADIUS);
    mask = max(mask, startCircle);

    vec2 endCircleOffset = polarToCartesian(radians((1.0 - angle) * 360.0), RADIUS - CIRCLE_RADIUS);
    float endCircle = circle(rotated + endCircleOffset, CIRCLE_RADIUS);
    mask = max(mask, endCircle);

    vec4 imageColor = fitImage(image, vUv, imageAspectRatio, imageScale);
    
    mask = max(mask, imageColor.a);
    vec3 color = mix(ringColor, imageColor.rgb, imageColor.a);

    gl_FragColor = vec4(color, mask);
}
`;
